/**
 * Het API-contract met de Worker, als typen.
 *
 * Dit bestand is de reden dat de frontend TypeScript is: de backend levert een
 * genormaliseerd model met vijf statussen, badges en een lijst concrete wijzigingen,
 * en de app hangt daar volledig aan. Zolang die vorm hier staat, breekt een
 * veldnaam die aan de serverkant verandert bij het bouwen in plaats van op een
 * wandtablet in de gang.
 *
 * Bron: api/src/zermelo/normalize.js en api/src/routes/rooster.js. Verandert daar
 * iets, dan hoort het hier mee te veranderen.
 */

/** De vijf UI-statussen. Zie docs/statussen.md. */
export type Status = 'normal' | 'cancelled' | 'changed' | 'new' | 'exam';

/** Eén concrete wijziging: "lokaal B04 → B09". */
export interface Wijziging {
  veld: 'lokaal' | 'docent' | 'uur' | 'groep';
  oud: string;
  nieuw: string;
}

export interface Les {
  id: number | string;
  instance: number;
  /** Lesuur; kan ontbreken bij een activiteit die niet op een lesuur valt. */
  uur: number | null;
  uurTot: number | null;
  start: string;
  eind: string;
  /** 'HH:MM' in de schooltijdzone — al door de backend berekend. */
  startTijd: string;
  eindTijd: string;
  vak: string;
  vakCode: string | null;
  docenten: string[];
  lokalen: string[];
  groepen: string[];
  opmerking: string;
  type: string;
  status: Status;
  /** Korte tekst voor het label rechts, of null bij een ongewijzigde les. */
  badge: string | null;
  wijzigingen: Wijziging[];
  toelichting: string;
}

/** Dagbrede activiteit die niet in één lesregel past — scenario 12. */
export interface Dagbanner {
  titel: string;
  toelichting: string;
  van: string;
  tot: string;
  lokalen: string[];
}

export interface Pauze {
  /** Na welk lesuur de pauze valt. */
  na: number;
  naam: string;
  start: string;
  end: string;
}

export interface Leerling {
  code: string;
  /**
   * Alleen de voornaam: de achternaam verlaat de server niet, want de tablet
   * hangt in de gang. Zie api/src/lib/naam.js.
   */
  voornaam: string | null;
  klas: string | null;
  niveau: string | null;
}

/**
 * De "laatst bijgewerkt"-informatie. Hoort altijd zichtbaar te zijn: bij een
 * storing blijft de laatst bekende stand staan, en dan moet het scherm eerlijk
 * kunnen melden dat de gegevens misschien niet meer kloppen.
 */
export interface Meta {
  bijgewerktOp: string | null;
  leeftijdSeconden: number | null;
  laatstePogingOp: string | null;
  verouderd: boolean;
}

/** Het antwoord van GET /api/rooster/:leerlingnummer. */
export interface Rooster {
  leerling: Leerling;
  dag: string;
  banner: Dagbanner | null;
  lessen: Les[];
  pauzes: Pauze[];
  meta: Meta;
}

/** Het deel van GET /api/status dat de tablet gebruikt. */
export interface ApiStatus {
  testdatum?: string | null;
  ok: boolean;
  databron: 'mock' | 'zermelo';
}

/**
 * Een fout van de API. `code` komt uit het `fout`-veld van de backend
 * (ONBEKEND_NUMMER, GEEN_DATA, …) en `status` is de HTTP-status; de app gebruikt
 * die twee om te beslissen tussen "melding tonen" en "terug naar inloggen".
 */
export class ApiFout extends Error {
  readonly code: string | null;
  readonly status: number | null;

  constructor(melding: string, code: string | null = null, status: number | null = null) {
    super(melding);
    this.name = 'ApiFout';
    this.code = code;
    this.status = status;
  }

  /**
   * Betekent deze fout dat het leerlingnummer niet (meer) klopt?
   *
   * Dan is doorpollen zinloos en horen we terug te gaan naar het inlogscherm — dat
   * kan gebeuren als een leerling uit het rooster verdwijnt terwijl de tablet
   * ingelogd staat.
   */
  get nummerOngeldig(): boolean {
    return this.status === 400 || this.status === 404 || this.code === 'ONBEKEND_NUMMER';
  }
}
